import MuxPlayer, { type MuxPlayerCSSProperties } from "@mux/mux-player-react";
import { Play, X } from "lucide-react";
import { useState, useSyncExternalStore } from "react";

const DISMISSED_KEY = "agentmrr_video_dismissed";

function readDismissed(): boolean {
	if (typeof window === "undefined") return false;
	return localStorage.getItem(DISMISSED_KEY) === "1";
}

const FULL_PLAYBACK_ID = "8aHCrJ1pf8va7dxrjRSctjxLQYAgMLkNTNXICqHEGHc";
const VIDEO_TITLE = "AgentMRR";

const playerStyle = {
	aspectRatio: "16 / 9",
	width: "100%",
	"--media-object-fit": "cover",
} as MuxPlayerCSSProperties;

const useIsClient = () =>
	useSyncExternalStore(
		() => () => {},
		() => true,
		() => false,
	);

function VideoCard({ onClose }: { onClose: () => void }) {
	const [started, setStarted] = useState(false);
	return (
		<div className="border border-border bg-popover">
			<div className="flex items-center justify-between border-b border-border px-4 py-2">
				<span className="font-mono text-sm uppercase tracking-[0.1em] text-muted-foreground">
					&gt; Get more customers
				</span>
				<button
					type="button"
					onClick={onClose}
					className="-mr-2 inline-flex size-10 items-center justify-center text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96]"
					aria-label="Dismiss"
				>
					<X className="size-3" />
				</button>
			</div>
			{started ? (
				<MuxPlayer
					playbackId={FULL_PLAYBACK_ID}
					streamType="on-demand"
					metadata={{ video_title: VIDEO_TITLE }}
					autoPlay
					playsInline
					accentColor="var(--brand)"
					style={playerStyle}
				/>
			) : (
				<button
					type="button"
					onClick={() => setStarted(true)}
					className="group relative block w-full overflow-hidden bg-muted"
					style={{ aspectRatio: "16 / 9" }}
					aria-label="Play video with sound"
				>
					<img
						src={`https://image.mux.com/${FULL_PLAYBACK_ID}/thumbnail.webp?fit_mode=smartcrop`}
						alt=""
						className="absolute inset-0 size-full object-cover"
						loading="lazy"
					/>
					<span className="absolute inset-0 flex items-center justify-center bg-background/10 transition-colors group-hover:bg-background/25">
						<span className="inline-flex size-12 items-center justify-center border border-foreground/20 bg-background/25 text-foreground/90 backdrop-blur-[2px] transition-transform group-hover:scale-105 group-active:scale-95">
							<Play className="size-5 translate-x-0.5 fill-current" />
						</span>
					</span>
				</button>
			)}
		</div>
	);
}

// Floating bottom-left. On mobile it sits above the fixed bottom ad marquee
// (which reserves ~4.5rem); on lg+ it drops to the bottom corner.
export function VideoWidget() {
	const isClient = useIsClient();
	const [hidden, setHidden] = useState(readDismissed);
	if (!isClient || hidden) return null;

	const dismiss = () => {
		localStorage.setItem(DISMISSED_KEY, "1");
		setHidden(true);
	};

	return (
		<div className="fixed z-50 bottom-[5rem] left-2 right-2 w-auto animate-in fade-in slide-in-from-bottom-4 duration-500 [animation-delay:1500ms] [animation-fill-mode:both] sm:left-4 sm:right-auto sm:w-[320px] xl:bottom-6">
			<VideoCard onClose={dismiss} />
		</div>
	);
}
